'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { usePageStore } from '@/store/usePageStore';

const NAV_IDX_KEY = '__navIdx_v1';

export function useNavigationEffects() {
  const pathname = usePathname();
  const search = useSearchParams();
  const fullPath = `${pathname}${search?.toString() ? `?${search.toString()}` : ''}`;

  const prevPathRef = useRef<string>(fullPath);

  // our own per-entry index + last pop direction sentinel
  const navIdxRef = useRef<number | null>(null);
  const lastPopDirectionRef = useRef<null | 'back' | 'forward'>(null);

  const setPreviousVisitedPath = (p: string | undefined) =>
    usePageStore.setState({ previousVisitedPath: p });

  // Boot: ensure current entry has our index (and sync it to refs/sessionStorage)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const readStored = () => {
      const raw = sessionStorage.getItem(NAV_IDX_KEY);
      const n = raw == null ? NaN : Number(raw);
      return Number.isFinite(n) ? n : 0;
    };

    let idx = readStored();
    const state = history.state ?? {};

    if (typeof state.__navIdx === 'number') {
      idx = state.__navIdx;
    } else {
      try {
        history.replaceState({ ...state, __navIdx: idx }, document.title);
      } catch {
        // ignore failures; not critical
      }
    }

    navIdxRef.current = idx;
    sessionStorage.setItem(NAV_IDX_KEY, String(idx));
  }, []);

  // Runs for *any* route change (push/replace/back/forward)
  useEffect(() => {
    const prev = prevPathRef.current;
    if (prev !== fullPath) {
      // keep previous path in store so you have it even on Back
      setPreviousVisitedPath(prev);

      // If NOT caused by popstate → push/replace
      if (lastPopDirectionRef.current === null) {
        // explicitly "not backwards"
        usePageStore.getState().setIsLastPopBackwards(false);

        // bump our index and stamp it on the current entry
        const nextIdx = (navIdxRef.current ?? 0) + 1;
        navIdxRef.current = nextIdx;
        sessionStorage.setItem(NAV_IDX_KEY, String(nextIdx));
        try {
          const state = history.state ?? {};
          history.replaceState({ ...state, __navIdx: nextIdx }, document.title);
        } catch {
          // ignore
        }
      }

      prevPathRef.current = fullPath;
      // clear after handling the route change triggered by a pop
      if (lastPopDirectionRef.current !== null) {
        lastPopDirectionRef.current = null;
      }
    }
  }, [fullPath]);

  // Handle native Back/Forward (popstate)
  useEffect(() => {
    const onPopState = (e: PopStateEvent) => {
      // keep your existing behavior
      usePageStore.getState().setPreviousPopPath(prevPathRef.current);

      const state = e.state ?? {};
      const hasIdx = typeof state.__navIdx === 'number';
      const currentIdx = navIdxRef.current;

      if (hasIdx && currentIdx !== null) {
        const newIdx = state.__navIdx as number;
        if (newIdx < currentIdx) {
          // BACK
          usePageStore.getState().setIsLastPopBackwards(true);
          lastPopDirectionRef.current = 'back';
        } else {
          // FORWARD or equal (unknown) -> not backwards
          usePageStore.getState().setIsLastPopBackwards(false);
          lastPopDirectionRef.current = 'forward';
        }
        navIdxRef.current = newIdx;
        sessionStorage.setItem(NAV_IDX_KEY, String(newIdx));
      } else {
        // Fallback: infer using your previousVisitedPath heuristic
        const prevVisited = usePageStore.getState().previousVisitedPath;
        const landing = location.pathname + (location.search ? `?${location.search.slice(1)}` : '');
        const isBack = prevVisited ? landing === prevVisited : false;

        usePageStore.getState().setIsLastPopBackwards(isBack);
        lastPopDirectionRef.current = isBack ? 'back' : 'forward';
        // (we don't change our index here because we don't know it)
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
}